import PropTypes from "prop-types";
import { useRef, useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, deleteDoc, updateDoc, arrayUnion, collection, getDocs } from "firebase/firestore";
import {
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import MemberDetail from "../../../management/MemberDetail";
import Iconify from "../../../components/Iconify";
import { db, functions } from "../../../firebase";

export default function UserMoreMenu({ member, menu, setMenu, onEdit }) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  const [memberDetail, setMemberDetail] = useState(null);

  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [potentialParents, setPotentialParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);

  useEffect(() => {
    if (showMemberDetail) {
      const fetchMemberDetail = async () => {
        try {
          const memberDocRef = doc(db, "members", member.id);
          const memberDocSnap = await getDoc(memberDocRef);
          if (memberDocSnap.exists()) {
            setMemberDetail({ id: memberDocSnap.id, ...memberDocSnap.data() });
          }
        } catch (error) {
          console.error("Error fetching member details:", error.message);
        }
      };
      fetchMemberDetail();
    }
  }, [showMemberDetail, member.id]);

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete ${member.firstName} ${member.lastName}?`
      )
    ) {
      try {
        const deleteSquareCustomer = httpsCallable(functions, "deleteSquareCustomer");
        if (member.squareCustomerId) {
          await deleteSquareCustomer({ customerId: member.squareCustomerId });
        }
        await deleteDoc(doc(db, "members", member.id));
        setMenu(!menu);
        alert("Member deleted successfully.");
      } catch (error) {
        console.error("Error deleting member:", error.message);
        alert("Failed to delete member. Please try again.");
      }
    }
  };


  const handleLinkToParentClick = () => {
    setIsOpen(false);
    setShowLinkDialog(true);
    setSearchQuery("");
    setPotentialParents([]);
    setSelectedParent(null);
  };

  const handleSearchParents = async () => {
    try {
      const q = searchQuery.toLowerCase();
      const snapshot = await getDocs(collection(db, "members"));
      const allMembers = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const filtered = allMembers.filter((pm) => {
        const fullName = `${pm.firstName || ""} ${pm.lastName || ""}`.toLowerCase();
        const email = (pm.email || "").toLowerCase();
        return fullName.includes(q) || email.includes(q);
      });
      setPotentialParents(filtered);
    } catch (err) {
      console.error("Error searching parents:", err.message);
    }
  };

  const handleSelectParent = (p) => {
    setSelectedParent(p);
  };

  const handleConfirmLink = async () => {
    if (!selectedParent) {
      alert("Please select a parent member first.");
      return;
    }

    try {
      await updateDoc(doc(db, "members", member.id), {
        parentId: selectedParent.id,
      });
      await updateDoc(doc(db, "members", selectedParent.id), {
        childrenIds: arrayUnion(member.id),
      });
      alert("Successfully linked child to parent.");
      setShowLinkDialog(false);
    } catch (err) {
      console.error("Error linking to parent:", err.message);
      alert("Failed to link child to parent. Please try again.");
    }
  };

  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 200, maxWidth: "100%" },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem sx={{ color: "text.primary" }} onClick={() => setShowMemberDetail(true)}>
          <ListItemIcon>
            <Iconify icon="eva:person-outline" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Member Details"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        <MenuItem
          sx={{ color: "text.primary" }}
          onClick={() => {
            setIsOpen(false);
            onEdit(member);
          }}
        >
          <ListItemIcon>
            <Iconify icon="eva:edit-fill" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Edit Member"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        <MenuItem sx={{ color: "text.primary" }} onClick={handleLinkToParentClick}>
          <ListItemIcon>
            <Iconify icon="ic:baseline-family-restroom" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Link to Parent"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        <MenuItem sx={{ color: "text.primary" }} onClick={handleDelete}>
          <ListItemIcon>
            <Iconify icon="eva:trash-2-outline" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={showMemberDetail}
        onClose={() => setShowMemberDetail(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          {memberDetail ? (
            <MemberDetail
              member={memberDetail}
              onClose={() => setShowMemberDetail(false)}
            />
          ) : (
            <Typography variant="h6">Loading member details...</Typography>
          )}
        </Box>
      </Dialog>

      <Dialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link to Parent Account</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Search for the parent member you want to link this child to.
          </Typography>
          <Box sx={{ display: "flex", mb: 2, gap: 1 }}>
            <TextField
              label="Search Parent Name/Email"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="contained" onClick={handleSearchParents}>
              Search
            </Button>
          </Box>

          {potentialParents.length > 0 && (
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {potentialParents.map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    p: 1,
                    mb: 1,
                    backgroundColor: selectedParent?.id === p.id ? "#eceff1" : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSelectParent(p)}
                >
                  <Typography variant="subtitle2">
                    {p.firstName} {p.lastName} ({p.email})
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmLink}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

UserMoreMenu.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    squareCustomerId: PropTypes.string,
  }).isRequired,
  menu: PropTypes.bool.isRequired,
  setMenu: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};
